// ============================================================================
// useContactConfig Hook - Fetch and cache contact configuration
// Allows clients to customize contact terminology and visibility
// Date: December 21, 2025
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/lib/authService';

export interface ContactTypeConfig {
  key: string;
  label: string;
}

export interface ContactConfig {
  id: number;
  client_id: number;
  
  // Main entity labels
  contacts_label: string;
  contacts_singular_label: string;
  contact_people_label: string;
  contact_people_singular_label: string;
  locations_label: string;
  locations_singular_label: string;
  documents_label: string;
  documents_singular_label: string;
  
  // Contact type configurations
  company_label: string;  // My Company - the client's own company (plural)
  company_singular_label: string;  // My Company - singular
  company_enabled: boolean;
  customer_label: string;
  customer_enabled: boolean;
  supplier_label: string;
  supplier_enabled: boolean;
  partner_label: string;
  partner_enabled: boolean;
  contractor_label: string;
  contractor_enabled: boolean;
  other_label: string;
  other_enabled: boolean;
  
  // Additional settings
  show_hierarchy_tab: boolean;
  default_contact_type: string;
  
  // Contact Form Settings
  contact_show_logo: boolean;
  contact_show_persona: boolean;
  contact_email_required: boolean;
  contact_mobile_required: boolean;
  contact_phone_visible: boolean;
  contact_website_visible: boolean;
  contact_require_contact_person: boolean;
  contact_require_credit_limit: boolean;
  
  // Person Form Settings
  person_show_photo: boolean;
  person_email_required: boolean;
  person_mobile_required: boolean;
  person_phone_visible: boolean;
  person_phone_required: boolean;
  person_check_whatsapp: boolean;
  person_show_wa_link: boolean;
  person_enable_total_connect: boolean;
  
  // Location Form Settings
  location_show_billing: boolean;
  location_require_contact: boolean;
  location_require_person: boolean;
  location_allow_free_text_address: boolean;
  location_code_visible: boolean;
  location_cost_centre_visible: boolean;
  location_types: string[];

  /** True when the visitor-management workflow is enabled for the client —
   *  controls visibility of the 'visitor' system contact type. */
  visitor_enabled: boolean;

  // Computed convenience array
  enabledTypes: ContactTypeConfig[];
}

// Default configuration (used while loading or on error)
export const DEFAULT_CONTACT_CONFIG: ContactConfig = {
  id: 0,
  client_id: 0,
  contacts_label: 'Stakeholders',
  contacts_singular_label: 'Stakeholder',
  contact_people_label: 'Contact People',
  contact_people_singular_label: 'Contact Person',
  locations_label: 'Locations',
  locations_singular_label: 'Location',
  documents_label: 'Documents',
  documents_singular_label: 'Document',
  company_label: 'My Company',  // Plural form (same as singular for this type)
  company_singular_label: 'My Company',
  company_enabled: true,
  customer_label: 'Customer',
  customer_enabled: true,
  supplier_label: 'Supplier',
  supplier_enabled: true,
  partner_label: 'Partner',
  partner_enabled: true,
  contractor_label: 'Contractor',
  contractor_enabled: true,
  other_label: 'Other',
  other_enabled: true,
  show_hierarchy_tab: true,
  default_contact_type: 'customer',
  // Contact Form Settings
  contact_show_logo: true,
  contact_show_persona: true,
  contact_email_required: false,
  contact_mobile_required: false,
  contact_phone_visible: true,
  contact_website_visible: true,
  contact_require_contact_person: false,
  contact_require_credit_limit: false,
  // Person Form Settings
  person_show_photo: true,
  person_email_required: false,
  person_mobile_required: false,
  person_phone_visible: true,
  person_phone_required: false,
  person_check_whatsapp: true,
  person_show_wa_link: true,
  person_enable_total_connect: false,
  // Location Form Settings
  location_show_billing: false,
  location_require_contact: false,
  location_require_person: false,
  location_allow_free_text_address: false,
  location_code_visible: true,
  location_cost_centre_visible: true,
  location_types: [],
  visitor_enabled: false,
  enabledTypes: [
    { key: 'company', label: 'My Company' },
    { key: 'customer', label: 'Customer' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'partner', label: 'Partner' },
    { key: 'contractor', label: 'Contractor' },
    { key: 'other', label: 'Other' }
  ]
};

// System contact types are not gated by contact_client_config — they are
// managed by their own features (e.g. visitor-management workflow).
const SYSTEM_CONTACT_TYPE_LABELS: Record<string, string> = {
  visitor: 'Visitor',
  employee: 'Employee',
  lead: 'Lead',
  prospect: 'Prospect',
};

// Simple in-memory cache
let configCache: { [clientId: number]: ContactConfig } = {};
let cacheTimestamps: { [clientId: number]: number } = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useContactConfig() {
  const { client, isAuthenticated } = useAuth();
  const [config, setConfig] = useState<ContactConfig>(DEFAULT_CONTACT_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated || !client?.id) {
      setConfig(DEFAULT_CONTACT_CONFIG);
      setIsLoading(false);
      return;
    }

    const clientId = client.id;
    const now = Date.now();

    // Check cache
    if (!forceRefresh && configCache[clientId] && cacheTimestamps[clientId]) {
      if (now - cacheTimestamps[clientId] < CACHE_TTL) {
        setConfig(configCache[clientId]);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const tokens = authService.getTokens();
      const response = await fetch('/api/v1/contacts/config', {
        headers: {
          'Authorization': `Bearer ${tokens?.access_token || ''}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contact configuration');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const fetchedConfig = data.data as ContactConfig;
        // Visitor is a system type gated by the visitor-management workflow —
        // the API only includes it in enabledTypes when the workflow is on.
        if (!Array.isArray(fetchedConfig.enabledTypes)) {
          fetchedConfig.enabledTypes = [];
        }
        if (!fetchedConfig.visitor_enabled) {
          fetchedConfig.enabledTypes = fetchedConfig.enabledTypes.filter(
            (t) => t.key !== 'visitor',
          );
        }
        configCache[clientId] = fetchedConfig;
        cacheTimestamps[clientId] = now;
        setConfig(fetchedConfig);
      } else {
        setConfig(DEFAULT_CONTACT_CONFIG);
      }
    } catch (err: any) {
      console.error('Error fetching contact config:', err);
      setError(err.message);
      setConfig(DEFAULT_CONTACT_CONFIG);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, client?.id]);

  const updateConfig = useCallback(async (updates: Partial<ContactConfig>): Promise<boolean> => {
    if (!isAuthenticated || !client?.id) {
      return false;
    }

    try {
      const tokens = authService.getTokens();
      const response = await fetch('/api/v1/contacts/config', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token || ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update configuration');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const updatedConfig = data.data as ContactConfig;
        configCache[client.id] = updatedConfig;
        cacheTimestamps[client.id] = Date.now();
        setConfig(updatedConfig);
        return true;
      }
      return false;
    } catch (err: any) {
      console.error('Error updating contact config:', err);
      setError(err.message);
      return false;
    }
  }, [isAuthenticated, client?.id]);

  const refreshConfig = useCallback(() => {
    return fetchConfig(true);
  }, [fetchConfig]);

  // Helper to get label for a contact type
  const getTypeLabel = useCallback((typeKey: string): string => {
    const labelMap: Record<string, keyof ContactConfig> = {
      company: 'company_label',
      customer: 'customer_label',
      supplier: 'supplier_label',
      partner: 'partner_label',
      contractor: 'contractor_label',
      other: 'other_label'
    };
    
    const labelKey = labelMap[typeKey];
    if (labelKey) return config[labelKey] as string;
    return SYSTEM_CONTACT_TYPE_LABELS[typeKey] || typeKey;
  }, [config]);

  // Helper to get plural label for a contact type (for filter buttons)
  // Company uses company_label directly, others get 's' added to singular
  const getTypeLabelPlural = useCallback((typeKey: string): string => {
    if (typeKey === 'company') {
      // Company uses the label directly (already in plural/proper form)
      return config.company_label || 'My Company';
    }
    // For other types, add 's' to make plural
    const singular = getTypeLabel(typeKey);
    return singular + 's';
  }, [config, getTypeLabel]);

  // Helper to check if a type is enabled
  const isTypeEnabled = useCallback((typeKey: string): boolean => {
    const enabledMap: Record<string, keyof ContactConfig> = {
      company: 'company_enabled',
      customer: 'customer_enabled',
      supplier: 'supplier_enabled',
      partner: 'partner_enabled',
      contractor: 'contractor_enabled',
      other: 'other_enabled'
    };
    
    const enabledKey = enabledMap[typeKey];
    if (enabledKey) return config[enabledKey] as boolean;
    // Visitor is gated by the visitor-management workflow (server-computed
    // visitor_enabled); other system types (employee, lead, prospect) are
    // not gated by contact_client_config.
    if (typeKey === 'visitor') return config.visitor_enabled === true;
    return typeKey in SYSTEM_CONTACT_TYPE_LABELS;
  }, [config]);

  // Initial fetch
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    isLoading,
    error,
    updateConfig,
    refreshConfig,
    getTypeLabel,
    getTypeLabelPlural,
    isTypeEnabled
  };
}

// Export a function to clear the cache (useful after logout)
export function clearContactConfigCache() {
  configCache = {};
  cacheTimestamps = {};
}
